import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError, formatPrice, formatDate, ORDER_STATUS } from "../lib/api";
import { useSeo } from "../lib/hooks";

const inputCls = "mt-1.5 w-full bg-transparent border border-[#C9A45C]/25 px-4 py-3 text-sm outline-none focus:border-[#C9A45C]";
const F = ({ label, testid, ...p }) => (
  <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">{label}</span>
    <input {...p} className={inputCls} data-testid={testid} /></label>
);

const AuthForms = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else if (mode === "register") await register(f);
      else { await api.post("/auth/forgot-password", { email: f.email }); toast.success("Si un compte existe, un e-mail de réinitialisation vient d'être envoyé."); setMode("login"); }
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const titles = { login: "Connexion", register: "Créer un compte", forgot: "Mot de passe oublié" };
  return (
    <div className="max-w-md mx-auto px-5 py-20" data-testid="account-auth">
      <p className="eyebrow mb-3">Mon compte</p>
      <h1 className="font-display text-4xl sm:text-5xl">{titles[mode]}</h1>
      <form onSubmit={submit} className="mt-10 space-y-4">
        {mode === "register" && (
          <div className="grid grid-cols-2 gap-3">
            <F label="Prénom" required value={f.first_name} onChange={set("first_name")} testid="register-first-name" />
            <F label="Nom" required value={f.last_name} onChange={set("last_name")} testid="register-last-name" />
          </div>
        )}
        <F label="E-mail" type="email" required value={f.email} onChange={set("email")} autoComplete="email" testid="account-email" />
        {mode !== "forgot" && <F label={mode === "register" ? "Mot de passe (8 caractères min.)" : "Mot de passe"} type="password" required minLength={mode === "register" ? 8 : undefined} value={f.password} onChange={set("password")} testid="account-password" />}
        <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="account-submit">{busy ? "…" : mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Recevoir le lien"}</button>
      </form>
      <div className="mt-6 flex flex-col gap-3 text-sm text-[#F8F6F1]/65">
        {mode !== "login" && <button onClick={() => setMode("login")} className="text-left hover:text-[#C9A45C]" data-testid="account-mode-login">J'ai déjà un compte</button>}
        {mode !== "register" && <button onClick={() => setMode("register")} className="text-left hover:text-[#C9A45C]" data-testid="account-mode-register">Créer un compte</button>}
        {mode === "login" && <button onClick={() => setMode("forgot")} className="text-left hover:text-[#C9A45C]" data-testid="account-mode-forgot">Mot de passe oublié ?</button>}
      </div>
      <p className="mt-6 text-xs text-[#F8F6F1]/40">La création d'un compte est facultative : vous pouvez commander sans compte.</p>
      <div className="mt-14 pt-6 border-t border-[#C9A45C]/10 text-center">
        <Link to="/admin/connexion" className="text-[10px] tracking-[0.3em] uppercase text-[#F8F6F1]/30 hover:text-[#C9A45C]" data-testid="account-admin-link">Administration</Link>
      </div>
    </div>
  );
};

const Profile = ({ user }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/account/profile").then((r) => { if (alive) setP({ phone: "", ...r.data, address: { line1: "", line2: "", postal_code: "", city: "", country: "France", ...(r.data.address || {}) } }); });
    return () => { alive = false; };
  }, []);
  if (!p) return null;
  const setA = (k) => (e) => setP({ ...p, address: { ...p.address, [k]: e.target.value } });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/account/profile", { first_name: p.first_name, last_name: p.last_name, phone: p.phone || null, address: p.address }); toast.success("Informations enregistrées"); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="profile-form">
      <F label="Prénom" required value={p.first_name || ""} onChange={(e) => setP({ ...p, first_name: e.target.value })} testid="profile-first-name" />
      <F label="Nom" required value={p.last_name || ""} onChange={(e) => setP({ ...p, last_name: e.target.value })} testid="profile-last-name" />
      <F label="Téléphone" value={p.phone || ""} onChange={(e) => setP({ ...p, phone: e.target.value })} testid="profile-phone" />
      <F label="E-mail" value={user.email} disabled />
      <F label="Adresse" value={p.address.line1} onChange={setA("line1")} testid="profile-address" />
      <F label="Complément" value={p.address.line2} onChange={setA("line2")} />
      <F label="Code postal" value={p.address.postal_code} onChange={setA("postal_code")} testid="profile-postal-code" />
      <F label="Ville" value={p.address.city} onChange={setA("city")} testid="profile-city" />
      <button type="submit" className="btn-gold sm:col-span-2" data-testid="profile-save">Enregistrer</button>
    </form>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);
  if (!orders) return <p className="eyebrow">Chargement…</p>;
  if (!orders.length) return <p className="text-sm text-[#F8F6F1]/55" data-testid="orders-empty">Aucune commande pour le moment.</p>;
  return (
    <div className="space-y-3" data-testid="account-orders">
      {orders.map((o) => (
        <Link key={o.id} to={`/suivi/${o.order_number}?t=${o.access_token}`} className="block border border-[#C9A45C]/20 p-4 hover:border-[#C9A45C] transition-colors" data-testid={`account-order-${o.order_number}`}>
          <div className="flex justify-between"><span className="font-display text-xl">{o.order_number}</span><span className="text-[#C9A45C]">{formatPrice(o.total)}</span></div>
          <p className="text-xs text-[#F8F6F1]/50 mt-1">{formatDate(o.created_at)} · {o.shipping.label} · <span className="text-[#E5C178]">{ORDER_STATUS[o.status]}</span>{o.payment_status === "paid" && <span className="text-emerald-400" data-testid={`account-order-paid-${o.order_number}`}> · Payée</span>}</p>
        </Link>
      ))}
    </div>
  );
};

export default function Account() {
  useSeo({ title: "Mon compte" });
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  if (user === null) return <p className="py-32 text-center eyebrow">Chargement…</p>;
  if (!user) return <AuthForms />;
  return (
    <div className="max-w-3xl mx-auto px-5 py-16" data-testid="account-page">
      <p className="eyebrow mb-3">Mon compte</p>
      <h1 className="font-display text-4xl sm:text-5xl">Bonjour {user.name}</h1>
      {user.role === "admin" && <Link to="/admin" className="btn-outline-gold !py-2 mt-4">Tableau de bord</Link>}
      <div className="flex gap-6 mt-8 border-b border-[#C9A45C]/15">
        {[["orders", "Mes commandes"], ["profile", "Mes informations"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} className={`pb-3 text-xs tracking-[0.2em] uppercase ${tab === id ? "text-[#C9A45C] border-b border-[#C9A45C]" : "text-[#F8F6F1]/55"}`} data-testid={`account-tab-${id}`}>{l}</button>
        ))}
        <button onClick={logout} className="ml-auto pb-3 text-xs text-[#F8F6F1]/45 hover:text-red-400" data-testid="account-logout">Déconnexion</button>
      </div>
      <div className="mt-8">{tab === "orders" ? <Orders /> : <Profile user={user} />}</div>
    </div>
  );
}

export function ResetPassword() {
  useSeo({ title: "Nouveau mot de passe" });
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Mot de passe modifié, vous pouvez vous connecter."); nav("/compte"); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-5 py-20 space-y-5" data-testid="reset-password-page">
      <h1 className="font-display text-4xl">Nouveau mot de passe</h1>
      <F label="Nouveau mot de passe (8 caractères min.)" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} testid="reset-password-input" />
      <button type="submit" className="btn-gold w-full" data-testid="reset-password-submit">Valider</button>
    </form>
  );
}
